/**
 * Types for the ATC CDN manifest format
 * 
 * Main manifest: https://cdn.atc.chillairwaves.com/manifest.json
 * Station manifest: https://cdn.atc.chillairwaves.com/{airport_code}/{station_type}/manifest.json
 */

import type { AtcRadioEffectPresetInput } from './AtcRadioEffect';

/**
 * A station entry in the main manifest
 */
export interface AtcStationInfo {
  /** ICAO airport code (e.g., "KJFK", "KSFO") */
  airport_code: string;
  /** Type of ATC station (e.g., "approach", "tower", "ground") */
  station_type: string;
  /** Number of available scenarios */
  scenario_count: number;
  /** Total size of all scenarios in bytes */
  total_size_bytes: number;
  /** URL to the station's manifest.json */
  manifest_url: string;

  /** Optional airport name */
  airport_name?: string | null;

  /** Optional IATA code */
  iata_code?: string | null;

  /** Optional ICAO code override */
  icao_code?: string | null;

  /** Optional local code */
  local_code?: string | null;

  /** Optional city */
  city?: string | null;

  /** Optional country */
  country?: string | null;

  /** Optional time zone */
  time_zone?: string | null;

  /** Optional station-specific radio effect preset payload */
  radio_effect_preset?: AtcRadioEffectPresetInput | null;
}

/**
 * Airport metadata entry in v2 root manifest
 */
export interface AtcAirportInfo {
  airport_code: string;
  name: string;
  icao_code: string;
  iata_code: string | null;
  local_code: string | null;
  city: string | null;
  country: string | null;
  time_zone: string | null;
  stations: AtcStationInfo[];
  station_count: number;
  total_scenario_count: number;
  total_size_bytes: number;
}

/**
 * Main manifest.json structure from the CDN
 * URL: https://cdn.atc.chillairwaves.com/manifest.json
 */
export interface AtcMainManifest {
  /** Flat list of available stations (legacy compatibility only) */
  stations?: AtcStationInfo[];
  /** Grouped airport entries (v2/v3) */
  airports?: AtcAirportInfo[];
  /** Number of airports (v2/v3) */
  airport_count?: number;
  /** Total size of all audio files in bytes */
  total_size_bytes: number;
  /** Total number of scenarios across all stations */
  total_scenario_count: number;
  /** Number of stations */
  station_count: number;
  /** Last update timestamp */
  updated_at: string;
  /** Manifest version */
  version: string;
}

/**
 * A scenario entry in a station manifest
 */
export interface AtcScenario {
  /** Unique scenario identifier (UUID) */
  scenario_id: string;
  /** File size in bytes */
  size_bytes: number;
  /** Upload timestamp */
  uploaded_at: string;
  /** Direct CDN URL to the MP3 file */
  cdn_url: string;
}

/**
 * Station manifest.json structure
 * URL: https://cdn.atc.chillairwaves.com/{airport_code}/{station_type}/manifest.json
 */
export interface AtcStationManifest {
  /** ICAO airport code */
  airport_code: string;
  /** Type of ATC station */
  station_type: string;
  /** List of available scenarios */
  scenarios: AtcScenario[];
  /** Total size of all scenarios in bytes */
  total_size_bytes: number;
  /** Number of scenarios */
  scenario_count: number;
  /** Last update timestamp */
  updated_at: string;
}

/**
 * Combined station info with loaded scenarios (for UI state)
 */
export interface AtcStation {
  /** ICAO airport code */
  airportCode: string;
  /** Type of ATC station */
  stationType: string;
  /** Display name (e.g., "KJFK Approach") */
  displayName: string;
  /** Number of available scenarios */
  scenarioCount: number;
  /** Total size in bytes */
  totalSizeBytes: number;
  /** URL to the station manifest */
  manifestUrl: string;
  /** Loaded scenarios (null if not yet loaded) */
  scenarios: AtcScenario[] | null;
  /** Last time the manifest was updated */
  updatedAt: string;

  /** Airport name */
  airportName?: string | null;
  /** IATA code */
  iataCode?: string | null;
  /** ICAO code */
  icaoCode?: string | null;
  /** Local code */
  localCode?: string | null;
  /** City */
  city?: string | null;
  /** Country */
  country?: string | null;
  /** Time zone */
  timeZone?: string | null;
  /** Optional station-specific radio effect preset payload */
  radioEffectPreset?: AtcRadioEffectPresetInput;
}

/**
 * Current playback state for a scenario
 */
export interface AtcPlaybackState {
  /** Currently selected station */
  station: AtcStation | null;
  /** Currently playing scenario */
  currentScenario: AtcScenario | null;
  /** Index in the scenarios array */
  currentScenarioIndex: number;
  /** Playback progress (0-100) */
  progress: number;
  /** Current time in seconds */
  currentTime: number;
  /** Total duration in seconds */
  duration: number;
  /** Is currently playing */
  isPlaying: boolean;
  /** Is loading */
  isLoading: boolean;
  /** Error message if any */
  error: string | null;
}

/**
 * Format bytes to human readable string
 */
export function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
}

/**
 * Generate display name for a station
 */
export function getStationDisplayName(airportCode: string, stationType: string): string {
  const stationTypeLabel = stationType.charAt(0).toUpperCase() + stationType.slice(1);
  return `${airportCode.toUpperCase()} ${stationTypeLabel}`;
}

export function getStationDisplayNameWithAirportName(
  airportCode: string,
  stationType: string,
  airportName?: string | null
): string {
  const stationTypeLabel = stationType.charAt(0).toUpperCase() + stationType.slice(1);
  const normalizedAirportName = airportName?.trim();
  const airportLabel = normalizedAirportName && normalizedAirportName.length > 0
    ? normalizedAirportName
    : airportCode.toUpperCase();

  return `${airportLabel} ${stationTypeLabel}`;
}

const INTERNATIONAL_WORD_REGEX = /\binternational\b/gi;

export function abbreviateInternationalForDisplay(name: string): string {
  return name.replace(INTERNATIONAL_WORD_REGEX, 'Intl');
}

export function getStationLabelWithoutType(station: Pick<AtcStation, 'airportName' | 'displayName' | 'stationType' | 'airportCode'>): string {
  const airportName = station.airportName?.trim();
  if (airportName) {
    return abbreviateInternationalForDisplay(airportName);
  }

  const displayName = station.displayName.trim();
  const stationType = station.stationType.trim();
  const stationTypeSuffix = ` ${stationType}`.toLowerCase();

  if (displayName.toLowerCase().endsWith(stationTypeSuffix)) {
    const labelWithoutType = displayName.slice(0, displayName.length - stationTypeSuffix.length).trim();
    return abbreviateInternationalForDisplay(labelWithoutType);
  }

  return abbreviateInternationalForDisplay(displayName || station.airportCode.toUpperCase());
}
